"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * The port of modal.js: one modal open at a time, opened by id. Triggers call
 * openModal() instead of carrying .js-trigger-modal and a data-target.
 *
 * The modals themselves are rendered once, by the site frame, and read their
 * open state from here.
 */

type ModalContextValue = {
  active: string | null;
  openModal: (id: string) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<string | null>(null);

  const openModal = useCallback((id: string) => setActive(id), []);
  const closeModal = useCallback(() => setActive(null), []);

  // The theme also opens a modal named in ?trigger_modal= on load.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("trigger_modal");

    if (id) {
      // Read after hydration on purpose: the server cannot see the query, and
      // opening it in the initial state would mismatch the server HTML.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActive(id);
    }
  }, []);

  useEffect(() => {
    if (!active) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActive(null);
      }
    };

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [active]);

  const value = useMemo(
    () => ({ active, openModal, closeModal }),
    [active, openModal, closeModal],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

export function useModals() {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error("useModals() must be used inside <ModalProvider>.");
  }

  return context;
}
